/**
 * «Мой бизнес»: список организаций владельца и вход в кабинет (ТЗ §14.1).
 *
 * Он же развилка онбординга: организаций нет — предлагаем создать первую.
 *
 * Список приходит из `GET /cabinet/businesses`, где владелец определяется подписью
 * `initData`, а не параметром запроса. Поэтому «чужих» организаций здесь появиться
 * не может, и фильтровать выдачу на клиенте нечем и незачем.
 */

import { useCallback, useState } from 'react';
import { presetFor } from '@app/shared';
import type { CabinetBusinessDto } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { IndustryTile, LangSwitch, Tag } from '../../ui/controls';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { splitByPublication } from './profile/businessList';

export function HomeScreen(props: {
  onOpenBusiness: (businessId: string) => void;
  onCreate: () => void;
  onBack: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();

  const { state, reload } = useAsync(
    useCallback(() => api.cabinet.listMyBusinesses(), [api]),
    [],
  );

  // «История» свёрнута по умолчанию: архив не рабочий, и разворачивать его при каждом
  // заходе значило бы возить владельцу на глаза то, что он сам убрал.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<unknown>(null);

  const groups = state.status === 'ready' ? splitByPublication(state.data) : null;
  // «Пусто» считается по живым организациям: тот, кто убрал единственную в историю,
  // должен снова увидеть предложение создать новую, а раздел «История» остаётся под ним.
  const empty = groups !== null && groups.drafts.length + groups.published.length === 0;

  const restore = async (businessId: string): Promise<void> => {
    if (restoringId) return;
    setRestoringId(businessId);
    setRestoreError(null);
    try {
      await api.cabinet.restoreBusiness(businessId);
      reload();
    } catch (failure) {
      setRestoreError(failure);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Screen>
      <TopBar
        title={t('bizHomeTitle')}
        subtitle={t('bizHomeSub')}
        onBack={props.onBack}
        right={<LangSwitch />}
      />

      <ScrollArea bottomGap="cta">
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {empty ? (
          <div className="px-4 pt-5">
            <EmptyState>{t('bizHomeEmpty')}</EmptyState>
          </div>
        ) : null}

        {/* Черновики первыми: именно они требуют действия владельца, а не просто работают. */}
        {groups && groups.drafts.length > 0 ? (
          <div className="px-4 pt-5">
            <SectionTitle>{t('bizSecDrafts')}</SectionTitle>
            {groups.drafts.map((item) => (
              <BusinessRow
                key={item.id}
                item={item}
                onOpen={() => props.onOpenBusiness(item.id)}
              />
            ))}
          </div>
        ) : null}

        {groups && groups.published.length > 0 ? (
          <div className="px-4 pt-5">
            <SectionTitle>{t('bizSecPublished')}</SectionTitle>
            {groups.published.map((item) => (
              <BusinessRow
                key={item.id}
                item={item}
                onOpen={() => props.onOpenBusiness(item.id)}
              />
            ))}
          </div>
        ) : null}

        {/* Пустого раздела нет вообще: заголовок без содержимого — шум. */}
        {groups && groups.history.length > 0 ? (
          <div className="px-4 pt-5">
            <button
              type="button"
              aria-expanded={historyOpen}
              onClick={() => setHistoryOpen(!historyOpen)}
              className="mb-2.5 flex w-full items-center justify-between text-left text-[12px] font-bold uppercase tracking-[0.07em] text-ink-3"
            >
              <span>
                {t('bizSecHistory')} ({groups.history.length})
              </span>
              <span aria-hidden="true">{historyOpen ? '▾' : '▸'}</span>
            </button>
            {historyOpen
              ? groups.history.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    busy={restoringId === item.id}
                    onRestore={() => void restore(item.id)}
                  />
                ))
              : null}
          </div>
        ) : null}
      </ScrollArea>

      <CtaBar>
        <CtaButton onClick={props.onCreate}>{t('bizCreate')}</CtaButton>
      </CtaBar>

      <BottomSheet
        open={restoreError !== null}
        title={t('errTitle')}
        onClose={() => setRestoreError(null)}
      >
        <ErrorState error={restoreError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setRestoreError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

/**
 * Карточка архива. Не открывается тапом: в кабинет архивной организации не заходят, её сначала
 * восстанавливают, и она возвращается ЧЕРНОВИКОМ — публикует владелец сам.
 */
function HistoryRow(props: {
  item: CabinetBusinessDto;
  busy: boolean;
  onRestore: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const { item } = props;

  return (
    <div className="mb-2.5 flex w-full items-center gap-3 rounded-card bg-surface px-3.5 py-3 shadow-[inset_0_0_0_1px_var(--hair)]">
      <IndustryTile type={presetFor(item.type).type} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-bold">{item.name}</div>
        <div className="mt-0.5 truncate text-[12px] text-ink-3">
          {item.address ?? t('addrNone')}
        </div>
      </div>
      <div className="flex w-[116px] shrink-0">
        <SmallButton variant="accent" disabled={props.busy} onClick={props.onRestore}>
          {t('bizRestore')}
        </SmallButton>
      </div>
    </div>
  );
}

function BusinessRow(props: { item: CabinetBusinessDto; onOpen: () => void }): JSX.Element {
  const { t } = useI18n();
  const { item } = props;
  // Пресет читается ТОЛЬКО ради иконки отрасли: её в строке организации нет вовсе.
  // Правила (шаг сетки, окно отмены, лимиты) берутся из самой строки — AGENTS.md, правило 5.
  const preset = presetFor(item.type);

  return (
    <button
      type="button"
      onClick={props.onOpen}
      className="mb-2.5 flex w-full items-center gap-3 rounded-card bg-surface px-3.5 py-3 text-left shadow-[inset_0_0_0_1px_var(--hair)]"
    >
      <IndustryTile type={preset.type} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-bold">{item.name}</div>
        <div className="mt-0.5 truncate text-[12px] text-ink-3">
          {item.address ?? t('addrNone')}
        </div>
      </div>
      <Tag tone={item.isPublished ? 'ok' : 'new'}>
        {item.isPublished ? t('bizPublished') : t('bizDraft')}
      </Tag>
      <span className="shrink-0 text-[18px] leading-none text-ink-3">›</span>
    </button>
  );
}
